<script setup lang="ts">
import { EditableArea } from "@magnolia/vue-editor";
import { useUser } from "@shopware-pwa/composables-next";
import { Heading } from "~/components/clarity-ui/Heading";
import { Paragraph } from "~/components/clarity-ui/Paragraph";
import { DashboardItem } from "~/components/MyBrita/Dashboard/DashboardItem";
import useMarketSetup from "~/composables/useMarketSetup";
import AccountGrid from "~/templates/components/account/AccountGrid.vue";
import AccountNewsletterRegistration from "~/templates/components/shop/account/AccountNewsletterRegistration.vue";
import CommonLabel from "~/templates/components/CommonLabel.vue";
import { isDummyUser } from "~/utils/helper/shop/user";

export interface AccountOverviewProps {
  accountOverviewNewsletter: object;
}

defineProps<AccountOverviewProps>();

defineOptions({
  inheritAttrs: false,
});

const { loadSalutation, user, userDefaultBillingAddress } = useUser();
const { marketSetupAdmin } = useMarketSetup();

const hasNoSalutation = computed(
  () =>
    user?.value?.salutation?.salutationKey === "not_specified" ||
    user?.value?.salutation?.salutationKey === "diverse",
);

onBeforeMount(async () => {
  try {
    if (user?.value?.salutationId) {
      await loadSalutation(user.value.salutationId);
    }
  } catch (e) {
    console.log("error ->", e);
  }
});
</script>

<template>
  <AccountGrid>
    <div class="account-overview-cards grid gap-4 xl:grid-cols-2 xl:gap-6">
      <div class="col-span-full mb-4">
        <Heading color="blue" tag="h1">
          <CommonLabel path="BWF/shop/account/overview/greeting" /><span>{{
            user?.firstName ? ",&nbsp;" + user?.firstName : ""
          }}</span>
        </Heading>
        <Paragraph class="mt-4">
          <CommonLabel path="BWF/shop/account/overview/mybritaOpeningText" />
        </Paragraph>
      </div>

      <DashboardItem>
        <div class="flex h-full flex-col gap-4">
          <Heading size="small" tag="h2">
            <CommonLabel path="BWF/shop/account/overview/myProfile" />
          </Heading>
          <div class="mb-2" data-no-tracking>
            <template v-if="!hasNoSalutation">{{
              user?.salutation?.translated?.displayName
            }}</template>
            {{ user?.firstName }}&nbsp;{{ user?.lastName }}
            <br />
            {{ user?.email }}
          </div>
          <NuxtLink
            class="btn btn-blue md:place-self-start xl:mt-auto"
            :to="marketSetupAdmin?.myBritaProfilePage ?? ''"
          >
            <CommonLabel path="BWF/shop/account/overview/editProfile" />
          </NuxtLink>
        </div>
      </DashboardItem>

      <DashboardItem>
        <div class="flex h-full flex-col gap-4">
          <Heading size="small" tag="h2">
            <CommonLabel path="BWF/shop/account/overview/myAddress" />
          </Heading>
          <AccountAddressCard
            v-if="userDefaultBillingAddress?.id && !isDummyUser(user)"
            :address="userDefaultBillingAddress"
            :show-title="false"
            :can-edit="false"
          />
          <Paragraph v-else class="mb-2">
            <CommonLabel path="BWF/shop/account/overview/noAddressAvailable" />
          </Paragraph>
          <NuxtLink
            class="btn btn-blue md:place-self-start xl:mt-auto"
            :to="marketSetupAdmin?.myBritaAddressPage ?? ''"
          >
            <CommonLabel
              v-if="userDefaultBillingAddress?.id"
              path="BWF/shop/account/overview/changeAddress"
            />
            <CommonLabel v-else path="BWF/shop/account/overview/createAddress" />
          </NuxtLink>
        </div>
      </DashboardItem>

      <DashboardItem>
        <div class="flex h-full flex-col gap-4">
          <Heading size="small" tag="h2">
            <CommonLabel path="BWF/shop/account/overview/orders" />
          </Heading>
          <Paragraph class="mb-2">
            <CommonLabel path="BWF/shop/account/overview/ordersExplanationTextLabel" />
          </Paragraph>
          <NuxtLink
            class="btn btn-blue md:place-self-start xl:mt-auto"
            :to="marketSetupAdmin?.myBritaOrderPage ?? ''"
          >
            <CommonLabel path="BWF/shop/account/overview/navigateToOrderHistory" />
          </NuxtLink>
        </div>
      </DashboardItem>

      <DashboardItem>
        <div class="flex h-full flex-col gap-4">
          <Heading size="small" tag="h2">
            <CommonLabel path="BWF/shop/account/overview/abonnementsLabel" />
          </Heading>
          <Paragraph class="mb-2">
            <CommonLabel path="BWF/shop/account/overview/abonnementsExplanationTextLabel" />
          </Paragraph>
          <NuxtLink
            class="btn btn-blue md:place-self-start xl:mt-auto"
            :to="marketSetupAdmin?.myBritaSubscriptionPage ?? ''"
          >
            <CommonLabel path="BWF/shop/account/overview/abonnements" />
          </NuxtLink>
        </div>
      </DashboardItem>

      <DashboardItem class="xl:col-span-full">
        <EditableArea
          :content="accountOverviewNewsletter"
          :custom-view="AccountNewsletterRegistration"
        />
      </DashboardItem>
    </div>
  </AccountGrid>
</template>
